import { createDeferredAction } from '@store/common/actions';
import { httpRequestFactory } from '@store/common/http-request-factory';
import { HttpRequestMethod } from '@store/common/http-request-method';
import { MAIN_API } from '@store/common/path';
import { getTopicsSelector } from '@store/topics/selectors';
import { ITopicsState } from '@store/topics/types';
import { TOPIC_LIMITS } from '@utils/pagination-limits';
import { AxiosResponse } from 'axios';
import { logError } from 'core/sentry';
import { IPaginationParams, ISearchTopicsRequest, ITopic } from 'lingopractices-models';
import { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';

import { GetTopicsSuccess } from './get-topics-success';

export interface IGetTopicsPayload {
  searchString: string;
  initializedByScroll: boolean;
}

export class GetTopics {
  static get action() {
    return createDeferredAction<IGetTopicsPayload>('topics/GET_TOPICS');
  }

  static get reducer() {
    return (draft: ITopicsState, { payload }: ReturnType<typeof GetTopics.action>) => {
      draft.requests.getTopicsPending = true;
      draft.searchString = payload.searchString;

      return draft;
    };
  }

  static get saga() {
    return function* ({ meta, payload }: ReturnType<typeof GetTopics.action>): SagaIterator {
      const topicList: ITopic[] = yield select(getTopicsSelector);

      const { searchString, initializedByScroll } = payload;

      const page: IPaginationParams = {
        offset: initializedByScroll ? topicList.length : 0,
        limit: TOPIC_LIMITS,
      };

      try {
        const { data } = GetTopics.httpRequest.call(
          yield call(() => GetTopics.httpRequest.generator({ page, name: searchString })),
        );

        const hasMore = data.length >= page.limit;

        yield put(GetTopicsSuccess.action({ data, hasMore, initializedByScroll }));
        meta?.deferred.resolve();
      } catch (e: any) {
        meta?.deferred.reject(e);
        logError(e);
      }
    };
  }

  static get httpRequest() {
    return httpRequestFactory<AxiosResponse<ITopic[]>, ISearchTopicsRequest>(
      MAIN_API.SEARCH_TOPICS,
      HttpRequestMethod.Post,
    );
  }
}
